import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import type { MouseEvent } from 'react';

import { EventType } from '@/analytics/types';
import type { ArticleCardDataProps } from '@/app/components/ArticleCardsModule/ArticleCard';
import SectionHeader from '@/app/components/SectionHeader';

import ArticleCardsDesktop from './ArticleCardsDesktop';
import ArticleCardsMobile from './ArticleCardsMobile';

const SCIENCE_DIRECT = 'ScienceDirect';
const NPJ_DIGITAL_MEDICINE = 'npj Digital Medicine';
const NATURE_AND_SCIENCE_OF_SLEEP = 'Nature and Science of Sleep';
const SENSORS = 'Sensors';
const PLOS_DIGITAL_HEALTH = 'PLOS Digital Health';
const SCIENCE_REPORTER = 'Science Reporter';
const ARTICLE_CLASS_NAME = 'w-[75vw] bg-sandstone-400 md:w-[60vw] lg:w-full';
const HEADING_CLASS_NAME = 'mb-6 text-xl';

const articleCards: ArticleCardDataProps[] = [
  {
    articleSource: {
      date: 'may_2025',
      siteName: SCIENCE_DIRECT,
    },
    buttonProps: {
      ariaLabel: 'read_more',
      siteName: SCIENCE_DIRECT,
      external: true,
      href: 'https://www.sciencedirect.com/science/article/abs/pii/S0016508525000137',
    },
    className: ARTICLE_CLASS_NAME,
    heading: 'science_research_2025_science_direct_headline',
    headingClassName: HEADING_CLASS_NAME,
  },
  {
    articleSource: {
      date: 'august_2024',
      siteName: NPJ_DIGITAL_MEDICINE,
    },
    buttonProps: {
      ariaLabel: 'read_more',
      siteName: NPJ_DIGITAL_MEDICINE,
      external: true,
      href: 'https://www.nature.com/articles/s41746-024-01183-9',
    },
    className: ARTICLE_CLASS_NAME,
    heading: 'science_research_2025_npj_headline',
    headingClassName: HEADING_CLASS_NAME,
  },
  {
    articleSource: {
      date: 'may_2025',
      siteName: NATURE_AND_SCIENCE_OF_SLEEP,
    },
    buttonProps: {
      ariaLabel: 'read_more',
      siteName: NATURE_AND_SCIENCE_OF_SLEEP,
      external: true,
      href: 'https://www.tandfonline.com/doi/pdf/10.2147/NSS.S511807',
    },
    className: ARTICLE_CLASS_NAME,
    heading: 'science_research_2025_nat_science_headline',
    headingClassName: HEADING_CLASS_NAME,
  },
  {
    articleSource: {
      date: 'august_2024',
      siteName: SENSORS,
    },
    buttonProps: {
      ariaLabel: 'read_more',
      siteName: SENSORS,
      external: true,
      href: 'https://www.mdpi.com/1424-8220/24/15/5024#:~:text=2.3.&text=Oura%20Ring%20smart%20rings%20were,Table%20A1%20in%20Appendix%20A',
    },
    className: ARTICLE_CLASS_NAME,
    heading: 'science_research_2025_sensors_headline',
    headingClassName: HEADING_CLASS_NAME,
  },
  {
    articleSource: {
      date: 'april_2024',
      siteName: PLOS_DIGITAL_HEALTH,
    },
    buttonProps: {
      ariaLabel: 'read_more',
      siteName: PLOS_DIGITAL_HEALTH,
      external: true,
      href: 'https://journals.plos.org/digitalhealth/article?id=10.1371/journal.pdig.0000473',
    },
    className: ARTICLE_CLASS_NAME,
    heading: 'science_research_2025_plos_headline',
    headingClassName: HEADING_CLASS_NAME,
  },
  {
    articleSource: {
      date: 'february_2024',
      siteName: SCIENCE_REPORTER,
    },
    buttonProps: {
      ariaLabel: 'read_more',
      siteName: SCIENCE_REPORTER,
      external: true,
      href: 'https://pubmed.ncbi.nlm.nih.gov/38316806/',
    },
    className: ARTICLE_CLASS_NAME,
    heading: 'science_research_2025_science_reporter_headline',
    headingClassName: HEADING_CLASS_NAME,
  },
];

const PublicationsSection = () => {
  const getPath = (event: MouseEvent<HTMLAnchorElement>): string | null => {
    const target = event.currentTarget;
    return target.getAttribute('href');
  };

  const sendAnalytics = async (
    event: MouseEvent<HTMLAnchorElement>,
    cta: string,
  ) => {
    const path = getPath(event);
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      cta,
      location: 'body',
      path,
    });
  };

  const handleClick = async (
    event: MouseEvent<HTMLAnchorElement>,
  ): Promise<void> => {
    event.preventDefault();
    const path = getPath(event);
    await sendAnalytics(event, 'view');
    window.open(path!, '_blank');
  };

  const handleHeaderButtonClick = async (
    event: MouseEvent<HTMLAnchorElement>,
  ): Promise<void> => {
    event.preventDefault();
    const target = event.currentTarget;
    const path = target.getAttribute('href');
    await sendAnalytics(event, 'oura research database');
    window.open(path!, '_blank');
  };

  return (
    <section className="pt-20" data-cy="publications-section">
      <SectionHeader
        title={{
          className: 'px-8',
          colEnd: { sm: 'main', md: 22, lg: 20 },
          colStart: { sm: 'main', md: 4, lg: 6 },
          key: 'science_research_2025_peer_review_title',
        }}
        copy={{
          className: 'px-4',
          colEnd: { sm: 'main', md: 21, lg: 19 },
          colStart: { sm: 'main', md: 5, lg: 7 },
          key: 'science_research_2025_peer_review_subtitle',
        }}
        button={{
          href: 'https://ouraring.com/blog/oura-in-research/',
          label: 'science_research_2025_peer_review_link',
          onClick: handleHeaderButtonClick,
          variant: 'secondary-dark',
        }}
      />
      <LayoutGrid className="pt-20">
        <GridItem colEnd={{ sm: 'full', lg: 'main' }} colStart={{ sm: 'main' }}>
          <ArticleCardsMobile
            articleCards={articleCards}
            onClick={handleClick}
          />
          <ArticleCardsDesktop
            articleCards={articleCards}
            onClick={handleClick}
          />
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default PublicationsSection;
